import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, ChevronRight, CheckCircle2, XCircle, AlertTriangle,
  Mail, Paperclip, Send, ExternalLink, Loader2, Link2,
} from "lucide-react";
import AssociateReplyDialog from "@/components/AssociateReplyDialog";
import { AttachmentChips, AttachmentPicker } from "@/components/Attachments";

const INTENT_META = {
  confirmada: { label: "Confirmada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  modificada: { label: "Confirmada con cambios", cls: "bg-orange-50 text-orange-700 border-orange-300" },
  necesita_info: { label: "Necesita info", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cotizacion: { label: "Cotización", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  cancelada: { label: "Cancelada / Rechazada", cls: "bg-red-50 text-red-700 border-red-200" },
  voucher_factura: { label: "Voucher / Factura", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  otro: { label: "Otro", cls: "bg-gray-50 text-gray-600 border-gray-200" },
};

const MATCH_LABELS = {
  hilo_app: { label: "Hilo de la app", cls: "bg-emerald-100 text-emerald-800" },
  "proveedor+cliente": { label: "Proveedor + cliente", cls: "bg-blue-100 text-blue-800" },
  solo_proveedor: { label: "Solo proveedor", cls: "bg-gray-100 text-gray-600" },
  cliente_ambiguo: { label: "Cliente ambiguo", cls: "bg-amber-100 text-amber-800" },
  manual: { label: "Asociada manualmente", cls: "bg-violet-100 text-violet-800" },
};

const fmtDt = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

/** Tarjeta completa de respuesta de proveedor: intent, datos, discrepancias,
 *  respuesta sugerida (editable + enviar), Aplicar a Sofi, factura y descartar.
 *  Usada en la bandeja /respuestas y en el hilo de cada reserva del itinerario. */
export default function ReplyCard({ reply, tab, onReviewed, onUpdated, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showBody, setShowBody] = useState(false);
  const [applying, setApplying] = useState(false);
  const [assocOpen, setAssocOpen] = useState(false);
  const ex = reply.extraction || {};
  const m = reply.matched || {};
  const [applyStatus, setApplyStatus] = useState(
    ["contacto", "reserva", "confirmacion", "cancelada"].includes(ex.accion_sugerida) ? ex.accion_sugerida : "reserva"
  );
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceDone, setInvoiceDone] = useState(
    (m.bookings || []).length > 0 && (m.bookings || []).every((b) => b.factura_solicitada)
  );
  const sug = ex.respuesta_sugerida || null;
  const [respSubject, setRespSubject] = useState(sug?.asunto || `Re: ${reply.subject || ""}`);
  const [respBody, setRespBody] = useState(sug?.cuerpo || "");
  const [sendingResp, setSendingResp] = useState(false);
  const [respondedAt, setRespondedAt] = useState(reply.responded_at || null);
  const [respAttachments, setRespAttachments] = useState([]);
  const [attachments, setAttachments] = useState(reply.attachments || []);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const intent = INTENT_META[ex.intent] || INTENT_META.otro;
  const match = MATCH_LABELS[m.match_method] || MATCH_LABELS.solo_proveedor;
  const hasBookings = (m.external_booking_ids || []).length > 0;

  const refetchAttachments = async () => {
    const { data } = await api.post(`/inbox/replies/${reply.id}/refetch-attachments`);
    setAttachments(data.attachments || []);
  };

  const toggleBody = async () => {
    if (showBody) { setShowBody(false); return; }
    setShowBody(true);
    if (thread || reply.simulated) return; // ya cargado o simulacro
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/inbox/replies/${reply.id}/thread`);
      setThread(data.messages || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el hilo completo");
      // Fallback: usaremos solo el correo del proveedor (reply.body)
      setThread([]);
    } finally {
      setLoadingThread(false);
    }
  };

  const sendResponse = async () => {
    const destino = reply.simulated
      ? "TU PROPIO correo (modo SIMULACRO — el proveedor real NO recibe nada)"
      : `${reply.from_name || ""} <${reply.from_email}>`;
    if (!window.confirm(
      `Se enviará este correo a:\n${destino}\n\nAsunto: ${respSubject}\n\n¿Confirmar envío?`
    )) return;
    setSendingResp(true);
    try {
      await api.post(`/inbox/replies/${reply.id}/respond`, {
        subject: respSubject, body: respBody,
        attachment_ids: respAttachments.map((a) => a.file_id),
      });
      toast.success("Respuesta enviada al proveedor");
      setRespondedAt(new Date().toISOString());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setSendingResp(false);
    }
  };

  const apply = async () => {
    const labels = { contacto: "CONTACTADO", reserva: "RESERVA", confirmacion: "CONFIRMACIÓN", cancelada: "CANCELADA" };
    const label = labels[applyStatus] || applyStatus;
    if (!window.confirm(
      `Se marcará${(m.external_booking_ids || []).length > 1 ? "n" : ""} ${(m.external_booking_ids || []).length} reserva(s) como ${label} en gestion (Sofi).\n\nProveedor: ${m.provider_name}\nCliente: ${m.client_name || "—"}\n\n¿Confirmar?`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/inbox/replies/${reply.id}/apply`, { new_status: applyStatus });
      const ok = data.results?.updated?.length || 0;
      const ko = data.results?.failed?.length || 0;
      toast.success(`Aplicado a Sofi — ${ok} actualizadas${ko ? `, ${ko} fallidas` : ""}`);
      onReviewed?.(reply.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar");
    } finally {
      setApplying(false);
    }
  };

  const markInvoice = async () => {
    if (!window.confirm(`Se marcará "Factura Solicitada = Sí" en Sofi para ${(m.external_booking_ids || []).length} reserva(s). ¿Confirmar?`)) return;
    setInvoiceLoading(true);
    try {
      const { data } = await api.post(`/inbox/replies/${reply.id}/invoice-requested`, {});
      const ok = data.results?.updated?.length || 0;
      const ko = data.results?.failed?.length || 0;
      toast.success(`Factura solicitada marcada en Sofi — ${ok} reservas${ko ? `, ${ko} fallidas` : ""}`);
      if (ok > 0) setInvoiceDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo marcar la factura");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm("¿Descartar esta respuesta? No se aplicará nada a Sofi.")) return;
    try {
      await api.post(`/inbox/replies/${reply.id}/dismiss`);
      toast.success("Respuesta descartada");
      onReviewed?.(reply.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descartar");
    }
  };

  return (
    <div className="vv-card" data-testid={`reply-card-${reply.id}`}>
      <button className="w-full p-3.5 flex items-start gap-3 text-left" onClick={() => setOpen(!open)}>
        <div className="mt-0.5">{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] border rounded-full px-2 py-0.5 font-medium ${intent.cls}`} data-testid={`reply-intent-${reply.id}`}>
              {intent.label}
            </span>
            {reply.simulated && (
              <span className="text-[10px] bg-purple-100 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5 font-medium">SIMULACRO</span>
            )}
            <span className="font-medium text-sm">{m.provider_name || reply.from_email}</span>
            {m.client_name && (
              <span className="text-xs text-gray-500">→ {m.client_name}</span>
            )}
            <span className={`text-[10px] rounded px-1.5 py-0.5 ${match.cls}`}>{match.label}</span>
            {ex.confianza && ex.confianza !== "alta" && (
              <span className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 rounded px-1.5 py-0.5">
                confianza {ex.confianza}
              </span>
            )}
            {respondedAt && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                <CheckCircle2 size={10} /> Respondida
              </span>
            )}
            <span className="text-[10px] text-gray-400 ml-auto">{fmtDt(reply.date)}</span>
          </div>
          <div className="text-xs text-gray-700 mt-1 truncate">{reply.subject}</div>
          {ex.resumen && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{ex.resumen}</p>}
          {(ex.discrepancias || []).length > 0 && (
            <div className="inline-flex items-center gap-1 mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
              <AlertTriangle size={11} /> {ex.discrepancias.length} discrepancia{ex.discrepancias.length > 1 ? "s" : ""}
            </div>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 ml-6 space-y-3">
          {(ex.discrepancias || []).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-900" data-testid={`reply-discrepancias-${reply.id}`}>
              <div className="flex items-center gap-1.5 font-medium mb-1"><AlertTriangle size={12} /> El proveedor confirma algo distinto a lo solicitado</div>
              <ul className="space-y-0.5 ml-4 list-disc">{ex.discrepancias.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </div>
          )}

          {ex.datos && Object.entries(ex.datos).some(([, v]) => v && (typeof v !== "object" || Object.keys(v).length)) && (
            <div className="flex flex-wrap gap-2 text-[11px]">
              {Object.entries(ex.datos).map(([k, v]) => {
                if (!v || (typeof v === "object" && !Object.keys(v).length)) return null;
                const val = typeof v === "object" ? JSON.stringify(v) : String(v);
                return (
                  <span key={k} className={`border rounded px-2 py-0.5 ${k === "tipo_habitacion" ? "bg-amber-50 border-amber-300 text-amber-900 font-medium" : "bg-blue-50 border-blue-200 text-blue-800"}`}>
                    {k.replace(/_/g, " ")}: <b>{val.slice(0, 70)}</b>
                  </span>
                );
              })}
            </div>
          )}

          {(attachments || []).length > 0 && (
            <div data-testid={`reply-attachments-${reply.id}`}>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
                <Paperclip size={11} /> Adjuntos del proveedor
              </div>
              <AttachmentChips
                attachments={attachments}
                testIdPrefix={`reply-att-${reply.id}`}
                onRefetch={reply.simulated ? undefined : refetchAttachments}
              />
            </div>
          )}

          {(sug?.cuerpo || respondedAt) && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3" data-testid={`reply-suggested-${reply.id}`}>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-blue-700 font-medium mb-2">
                <Send size={11} /> Respuesta planteada por la IA
                {respondedAt && (
                  <span className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                    <CheckCircle2 size={11} /> Enviada {respondedAt.slice(0, 16).replace("T", " ")}
                  </span>
                )}
              </div>
              {!respondedAt && tab === "pending" ? (
                <div className="space-y-2">
                  <input
                    value={respSubject}
                    onChange={(e) => setRespSubject(e.target.value)}
                    className="w-full h-8 text-xs border border-gray-200 rounded-md px-2 bg-white"
                    data-testid={`reply-resp-subject-${reply.id}`}
                  />
                  <textarea
                    value={respBody}
                    onChange={(e) => setRespBody(e.target.value)}
                    rows={Math.min(10, Math.max(4, (respBody.match(/\n/g) || []).length + 2))}
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white font-sans"
                    data-testid={`reply-resp-body-${reply.id}`}
                  />
                  <AttachmentPicker attachments={respAttachments} onChange={setRespAttachments} compact testIdPrefix={`reply-resp-att-${reply.id}`} />
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={sendResponse} disabled={sendingResp || !respBody.trim()}
                      className="h-8 bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`reply-resp-send-${reply.id}`}>
                      {sendingResp ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
                      {reply.simulated ? "Enviar respuesta (SIMULACRO → a tu propio correo)" : `Enviar respuesta a ${reply.from_email}`}
                    </Button>
                    {respBody.includes("[COMPLETAR") && (
                      <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                        ⚠ Revisa los huecos [COMPLETAR] antes de enviar
                      </span>
                    )}
                  </div>
                </div>
              ) : !respondedAt ? (
                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-700">{sug?.cuerpo}</pre>
              ) : null}
            </div>
          )}

          {(m.bookings || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Reservas asociadas</div>
              <div className="space-y-1">
                {m.bookings.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 text-xs">
                    <span className="text-[10px] bg-gray-100 rounded px-1.5 py-0.5">{b.service_type}</span>
                    <span className="text-gray-500">{b.date}</span>
                    <span className="text-gray-800 truncate">{(b.description || "").slice(0, 70)}</span>
                    {b.price != null && (
                      <span className="text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5 font-medium whitespace-nowrap">
                        Sofi: {b.price} €
                      </span>
                    )}
                    {b.sofi_status && b.sofi_status !== "sin" && (
                      <span className="text-[10px] bg-gray-50 border border-gray-200 text-gray-600 rounded px-1.5 py-0.5 uppercase">{b.sofi_status}</span>
                    )}
                    {b.factura_solicitada && (
                      <span className="text-[10px] bg-teal-50 border border-teal-200 text-teal-700 rounded px-1.5 py-0.5">factura ✓</span>
                    )}
                    {b.external_booking_id && <span className="text-[10px] text-gray-400">#{b.external_booking_id}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {m.external_trip_id && (
            <a href={`https://gestion.viajadverdad.com/trips/form/1/${m.external_trip_id}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
              <ExternalLink size={11} /> Abrir trip {m.external_trip_id} en gestion
            </a>
          )}

          <div>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={toggleBody}
              data-testid={`reply-body-btn-${reply.id}`}>
              <Mail size={12} className="mr-1" /> {showBody ? "Ocultar correo" : "Ver hilo completo"}
            </Button>
            {showBody && (
              <div className="mt-2 space-y-2">
                {loadingThread && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500 flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" /> Descargando hilo completo de IMAP…
                  </div>
                )}
                {!loadingThread && (thread && thread.length > 0 ? (
                  <>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 px-1">
                      {thread.length} mensaje{thread.length > 1 ? "s" : ""} en el hilo (ordenados por fecha)
                    </div>
                    {thread.map((msg, i) => {
                      const isProv = msg.direction !== "agencia";
                      return (
                        <div key={i}
                          className={`rounded-lg border p-2.5 text-xs ${isProv ? "border-gray-200 bg-gray-50" : "border-blue-200 bg-blue-50/30"}`}
                          data-testid={`reply-thread-msg-${reply.id}-${i}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="text-[10px] text-gray-500 truncate">
                              <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider ${isProv ? "bg-gray-200 text-gray-700" : "bg-blue-200 text-blue-800"}`}>
                                {isProv ? "Proveedor" : "Agencia"}
                              </span>
                              {msg.from} · {fmtDt(msg.date)}
                            </div>
                          </div>
                          {msg.subject && i === 0 && (
                            <div className="font-medium text-gray-800 mb-1">{msg.subject}</div>
                          )}
                          <pre className="whitespace-pre-wrap font-sans text-gray-700 max-h-72 overflow-y-auto">{msg.body || "(sin texto)"}</pre>
                          {(msg.attachments || []).length > 0 && (
                            <div className="mt-1.5 text-[10px] text-gray-500">
                              📎 {msg.attachments.map((a) => (typeof a === "string" ? a : a.filename)).join(" · ")}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  // Fallback: solo el correo del proveedor (cuando no hay thread o simulacro)
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs">
                    <div className="text-[10px] text-gray-500 mb-1">{reply.from_name} &lt;{reply.from_email}&gt; · {fmtDt(reply.date)}</div>
                    <div className="font-medium text-gray-800 mb-1">{reply.subject}</div>
                    <pre className="whitespace-pre-wrap font-sans text-gray-700 max-h-64 overflow-y-auto">{reply.body || "(sin texto)"}</pre>
                    {(attachments || []).length > 0 && (
                      <div className="mt-1.5">
                        <AttachmentChips
                          attachments={attachments}
                          testIdPrefix={`reply-body-att-${reply.id}`}
                          onRefetch={reply.simulated ? undefined : refetchAttachments}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {tab === "pending" && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
              {hasBookings ? (
                <>
                  <select value={applyStatus} onChange={(e) => setApplyStatus(e.target.value)}
                    className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white"
                    data-testid={`reply-status-select-${reply.id}`}>
                    <option value="contacto">Estado Sofi: Contacto</option>
                    <option value="reserva">Estado Sofi: Reserva</option>
                    <option value="confirmacion">Estado Sofi: Confirmación</option>
                    <option value="cancelada">Estado Sofi: Cancelada</option>
                  </select>
                  <Button size="sm" onClick={apply} disabled={applying}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`reply-apply-${reply.id}`}>
                    {applying ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
                    Aplicar a Sofi
                  </Button>
                  {invoiceDone ? (
                    <span className="inline-flex items-center gap-1 text-[11px] bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5">
                      <CheckCircle2 size={11} /> Factura solicitada
                    </span>
                  ) : (
                    <Button variant="outline" size="sm" onClick={markInvoice} disabled={invoiceLoading}
                      className="h-8 text-xs border-teal-500 text-teal-700 hover:bg-teal-50" data-testid={`reply-invoice-${reply.id}`}>
                      {invoiceLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                      Marcar factura solicitada
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <span className="text-[11px] text-gray-400">Sin reservas de gestion asociadas</span>
                  <Button variant="outline" size="sm" onClick={() => setAssocOpen(true)}
                    className="h-8 text-xs border-violet-400 text-violet-700 hover:bg-violet-50"
                    data-testid={`reply-associate-${reply.id}`}>
                    <Link2 size={12} className="mr-1" /> Asociar a un viaje
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={dismiss}
                className="h-8 text-xs text-gray-600" data-testid={`reply-dismiss-${reply.id}`}>
                <XCircle size={12} className="mr-1" /> Descartar
              </Button>
            </div>
          )}

          {tab !== "pending" && (
            <div className="text-[11px] text-gray-400 pt-1 border-t border-gray-100 flex items-center gap-1.5">
              <CheckCircle2 size={12} className={reply.status === "applied" ? "text-emerald-500" : "text-gray-400"} />
              {reply.status === "applied" ? "Aplicada" : "Descartada"} por {reply.reviewed_by} · {fmtDt(reply.reviewed_at)}
            </div>
          )}
        </div>
      )}

      {assocOpen && (
        <AssociateReplyDialog reply={reply} open={assocOpen} onOpenChange={setAssocOpen} onUpdated={onUpdated} />
      )}
    </div>
  );
}
